import type { StreamEvent } from '@ralph/contracts'
import { useCallback, useReducer, useRef } from 'react'
import { ApiRequestError, apiSend } from '@/api/client.ts'
import { resumeRunStream, streamMessage } from '@/api/stream.ts'
import { describeIssue } from '@/i18n/index.ts'
import type { Messages } from '@/i18n/messages/index.ts'
import type { RunFailure, SendResult } from './run-stream-reducer.ts'
import { initialState, reduce } from './run-stream-reducer.ts'

// Re-exported so the reducer's split into its own module is an internal detail: components
// keep importing the trace types from the hook they already use.
export type { PendingApproval, RunFailure, RunStreamState, SendResult, TraceNode } from './run-stream-reducer.ts'

/**
 * What went wrong with a stream, as identifiers — never as a sentence.
 *
 * This is the only place the `ApiRequestError` still exists, so it is where `messageKey` and
 * its parameters are lifted off the wire and handed to the reducer; every surface downstream
 * renders them through the catalogue (PRD-27 D-4).
 *
 * Nothing here picks words. A hook runs outside any component, so there is no locale to ask,
 * and a sentence chosen here would be English inside a Portuguese conversation — which is why
 * a lost connection is named by its key rather than written out.
 */
const failureOf = (cause: unknown): RunFailure => {
  if (cause instanceof ApiRequestError) {
    const issue = cause.issues[0]
    return {
      code: cause.code,
      message: cause.message,
      messageKey: issue?.messageKey ?? null,
      params: issue?.params ?? null
    }
  }

  // An aborted fetch, a `TypeError` from the network, a socket that closed mid-event: the
  // server said nothing, so its line is whatever the browser wrote, and the sentence a person
  // reads is this product's own.
  const detail = cause instanceof Error ? cause.message : ''
  return {
    code: 'stream_lost',
    message: detail === '' ? 'The connection to the assistant was lost.' : detail,
    messageKey: 'common.connection_lost',
    params: null
  }
}

/**
 * A run's failure as a sentence in the reader's language.
 *
 * Lives beside the shape it renders rather than in each of the two surfaces that show one —
 * the conversation and the trace lane — so both say the same thing about the same failure.
 * A key this build does not know falls back to the server's English, which is why `message`
 * is on the wire and never removed (PRD-27 D-4, LOC-033).
 */
export const describeRunFailure = (t: Messages, failure: RunFailure): string =>
  describeIssue(t, failure) ?? (failure.message || t.common.genericError)

export const useRunStream = () => {
  const [state, dispatch] = useReducer(reduce, initialState)
  const abortRef = useRef<AbortController | null>(null)
  const seqRef = useRef(-1)

  const onEvent = useCallback((event: StreamEvent) => {
    seqRef.current = Math.max(seqRef.current, event.seq)
    dispatch({ type: 'event', event })
  }, [])

  const reset = useCallback(() => {
    abortRef.current?.abort()
    abortRef.current = null
    seqRef.current = -1
    dispatch({ type: 'reset' })
  }, [])

  const send = useCallback(
    async (conversationId: string, content: string): Promise<SendResult> => {
      reset()
      // Synchronously, before any await: the state must say a send is in flight from this
      // exact moment, or every "is a run active?" check between here and the first SSE byte
      // reads idle — see the send-start note in run-stream-reducer.ts for the abort race
      // that resulted.
      dispatch({ type: 'send-start' })
      const controller = new AbortController()
      abortRef.current = controller

      // Whether the server ever acknowledged the message. A request refused outright — a
      // throttle, an expired session, a dropped connection — created nothing, so the caller
      // can safely hand the question back to the composer. Once events start arriving the
      // run exists and is billable, and re-offering the text would invite a duplicate.
      let accepted = false
      const observe = (event: StreamEvent) => {
        accepted = true
        onEvent(event)
      }

      try {
        await streamMessage(
          conversationId,
          {
            content,
            // Generated client-side so a retried POST resolves to the run already created
            // rather than starting (and billing) a second one.
            idempotencyKey: crypto.randomUUID()
          },
          { onEvent: observe, signal: controller.signal }
        )
        return { ok: true, accepted: true }
      } catch (err) {
        if (controller.signal.aborted) return { ok: false, accepted }
        dispatch({ type: 'error', failure: failureOf(err) })
        return { ok: false, accepted }
      }
    },
    [onEvent, reset]
  )

  /**
   * Replays a finished run from the durable log.
   *
   * Deliberately the same reducer the live stream feeds: a trace viewed an hour later
   * renders identically to one watched as it happened, because it is literally the same
   * events in the same order. Rebuilding a second read-model from `/api/runs/:id` would be
   * a parallel implementation that drifts.
   */
  const replay = useCallback(
    async (runId: string) => {
      abortRef.current?.abort()
      const controller = new AbortController()
      abortRef.current = controller
      seqRef.current = -1
      dispatch({ type: 'replay-start' })

      try {
        await resumeRunStream(runId, -1, { onEvent, signal: controller.signal })
      } catch {
        // A trace that cannot be loaded is not worth an error state; the answer above it
        // is still correct and readable.
      }
    },
    [onEvent]
  )

  /** Reattaches after an approval decision, or after the tab was backgrounded and dropped. */
  const reattach = useCallback(
    async (runId: string) => {
      const controller = new AbortController()
      abortRef.current = controller
      try {
        await resumeRunStream(runId, seqRef.current, { onEvent, signal: controller.signal })
      } catch (err) {
        if (controller.signal.aborted) return
        dispatch({ type: 'error', failure: failureOf(err) })
      }
    },
    [onEvent]
  )

  /**
   * Stop.
   *
   * Aborting the fetch and dispatching nothing was a wedge: `status` stayed `running`, so
   * `isStreaming` never went false, the button never came back to Send, and the composer's own
   * guard refused every later message until the conversation changed. The local settle is
   * therefore unconditional and happens first — the UI must recover whatever the network does.
   *
   * The POST is the other half, because abandoning the stream does not stop the run: it keeps
   * working and keeps billing, and a Stop that only stops the watching is a lie. Cancellation
   * is cooperative server-side (the run observes the flag at its next boundary), so this is
   * fire-and-forget: a refusal — the run finished a millisecond ago, someone else's run — is
   * not worth putting in front of the reader, and must never leave the composer stuck again.
   *
   * DECISION: `POST /api/runs/:id/cancel` already exists (routes/runs.ts) and is owner-scoped,
   * so nothing was invented here — but it does make Stop stop the run and not merely the
   * watching. Drop the POST and keep the dispatch if a client-only fix was the intended scope.
   */
  const cancel = useCallback(() => {
    abortRef.current?.abort()
    abortRef.current = null
    dispatch({ type: 'cancelled' })

    const runId = state.runId
    if (!runId) return
    void apiSend(`/api/runs/${runId}/cancel`, 'POST').catch(() => {
      // Nothing to say. The reader has already left this run.
    })
  }, [state.runId])

  const isStreaming = state.status === 'running' || state.status === 'queued'

  return { state, send, reattach, replay, reset, cancel, isStreaming }
}
